/**
 * Note: Much of this file is a copy of alien signals' higher-level abstractions.
 *       The biggest differences from it are:
 *       - Removal of the effect scopes abstraction - it is not needed for `fluidstate`
 *       - Replacement of the signals abstraction with the atom abstraction (i.e. a signal
 *         without value)
 *       - Adding the ability to schedule effects
 *       - Adding the ability to have "onBecomeObservedListener" and
 *         "onBecomeUnobservedListener" listeners on atoms and computeds
 *       - Adding the ability to customize `equals` check for computeds
 *         and replacing the `===` default equals check with `Object.is`
 */
import {
	createReactiveSystem,
	ReactiveNode,
	ReactiveFlags,
	Link,
} from "alien-signals";
import { AtomOptions, ComputedAtomOptions, ReactionOptions } from "fluidstate";

export {
	AtomOptions,
	ComputedAtomOptions,
	ReactionOptions,
	createReactiveSystem,
	ReactiveNode,
	ReactiveFlags,
	Link,
};

const enum EffectFlags {
	Queued = 1 << 6,
}

export enum AtomType {
	Atom,
	Computed,
	Effect,
}

export interface Effect extends ReactiveNode {
	isInitialized: boolean;
	isStopped: boolean;
	atomType: AtomType.Effect;
	fn(): void;
	computed: Computed<void>;
	options?: ReactionOptions;
}

export interface Computed<T = any> extends ReactiveNode {
	atomType: AtomType.Computed;
	value: T | undefined;
	getter: (previousValue?: T) => T;
	watchCount: number;
	options?: ComputedAtomOptions;
}

export interface Atom extends ReactiveNode {
	atomType: AtomType.Atom;
	watchCount: number;
	options?: AtomOptions;
}

export const isComputed = (node: ReactiveNode): node is Computed<unknown> => {
	return "atomType" in node && node.atomType === AtomType.Computed;
};

export const isEffect = (node: ReactiveNode): node is Effect => {
	return "atomType" in node && node.atomType === AtomType.Effect;
};

export const isAtom = (node: ReactiveNode): node is Atom => {
	return "atomType" in node && node.atomType === AtomType.Atom;
};

const queuedEffects: (Effect | undefined)[] = [];
export const {
	link,
	unlink,
	propagate,
	checkDirty,
	endTracking,
	startTracking,
	shallowPropagate,
} = createReactiveSystem({
	update(signal: Atom | Computed): boolean {
		if (isComputed(signal)) {
			return updateComputed(signal);
		} else {
			updateAtom(signal);
			return true;
		}
	},
	notify,
	unwatched(node: Atom | Computed | Effect) {
		if (isComputed(node)) {
			let toRemove = node.deps;
			if (toRemove !== undefined) {
				node.flags = 17 as ReactiveFlags.Mutable | ReactiveFlags.Dirty;
				do {
					toRemove = unlink(toRemove, node);
				} while (toRemove !== undefined);
			}
		} else if (!isAtom(node)) {
			effectOper.call(node);
		}
	},
});

export let batchDepth = 0;

let notifyIndex = 0;
let queuedEffectsLength = 0;
let activeSub: ReactiveNode | undefined;

export function getCurrentSub(): ReactiveNode | undefined {
	return activeSub;
}

export function setCurrentSub(sub: ReactiveNode | undefined) {
	const prevSub = activeSub;
	activeSub = sub;
	return prevSub;
}

export function startBatch() {
	++batchDepth;
}

export function endBatch() {
	if (!--batchDepth) {
		flush();
	}
}

export function atom(options?: AtomOptions): Atom {
	return {
		atomType: AtomType.Atom,
		subs: undefined,
		subsTail: undefined,
		flags: 1 satisfies ReactiveFlags.Mutable,
		watchCount: 0,
		options,
	};
}

export const computedCore = <T>(
	getter: (previousValue?: T) => T,
	options?: ComputedAtomOptions
): { computed: Computed<T>; get: () => T } => {
	const computed: Computed<T> = {
		atomType: AtomType.Computed,
		value: undefined,
		subs: undefined,
		subsTail: undefined,
		deps: undefined,
		depsTail: undefined,
		flags: 17 as ReactiveFlags.Mutable | ReactiveFlags.Dirty,
		getter,
		watchCount: 0,
		options,
	};
	const get = computedOper.bind(computed as Computed<unknown>);
	return {
		computed,
		get: get as () => T,
	};
};

export function computed<T>(
	getter: (previousValue?: T) => T,
	options?: ComputedAtomOptions
): () => T {
	return computedCore(getter, options)["get"];
}

export function effect(fn: () => void, options?: ReactionOptions): () => void {
	const { get, computed } = computedCore(fn, { equals: () => false });
	const e: Effect = {
		isInitialized: false,
		isStopped: false,
		atomType: AtomType.Effect,
		fn: get,
		computed,
		subs: undefined,
		subsTail: undefined,
		deps: undefined,
		depsTail: undefined,
		flags: 2 satisfies ReactiveFlags.Watching,
		options,
	};
	if (e.options?.scheduler) {
		e.options.scheduler(runInitialEffect.bind(null, e));
	} else {
		runInitialEffect(e);
	}
	return effectOper.bind(e);
}

export function runInitialEffect(e: Effect) {
	if (e.isStopped) {
		return;
	}
	onWatched(e.computed);
	if (activeSub !== undefined) {
		link(e, activeSub);
	}
	const prev = setCurrentSub(e);
	try {
		e.fn();
	} finally {
		setCurrentSub(prev);
	}
}

function updateComputed(c: Computed): boolean {
	const prevSub = setCurrentSub(c);
	startTracking(c);
	try {
		const oldValue = c.value;
		return !(c.options?.equals ?? Object.is)(
			oldValue,
			(c.value = c.getter(oldValue))
		);
	} finally {
		if (c.watchCount) {
			for (
				let link = c.depsTail !== undefined ? c.depsTail.nextDep : c.deps;
				link !== undefined;
				link = link.nextDep
			) {
				const dep = link.dep;
				onUnwatched(dep);
			}
		}
		setCurrentSub(prevSub);
		endTracking(c);
	}
}

function updateAtom(s: Atom) {
	s.flags = 1 satisfies ReactiveFlags.Mutable;
}

function notify(e: Effect) {
	const flags = e.flags;
	if (!(flags & EffectFlags.Queued)) {
		e.flags = flags | EffectFlags.Queued;
		const subs = e.subs;
		if (subs !== undefined) {
			notify(subs.sub as Effect);
		} else {
			queuedEffects[queuedEffectsLength++] = e;
		}
	}
}

function run(e: Effect, flags: ReactiveFlags): void {
	if (
		flags & (16 satisfies ReactiveFlags.Dirty) ||
		(flags & (32 satisfies ReactiveFlags.Pending) && checkDirty(e.deps!, e))
	) {
		const prev = setCurrentSub(e);
		startTracking(e);
		try {
			e.fn();
		} finally {
			setCurrentSub(prev);
			endTracking(e);
		}
		return;
	} else if (flags & (32 satisfies ReactiveFlags.Pending)) {
		e.flags = flags & ~(32 satisfies ReactiveFlags.Pending);
	}
	let link = e.deps;
	while (link !== undefined) {
		const dep = link.dep;
		const depFlags = dep.flags;
		if (isEffect(dep) && depFlags & EffectFlags.Queued) {
			run(dep, (dep.flags = depFlags & ~EffectFlags.Queued));
		}
		link = link.nextDep;
	}
}

function flush(): void {
	while (notifyIndex < queuedEffectsLength) {
		const effect = queuedEffects[notifyIndex]!;
		queuedEffects[notifyIndex++] = undefined;
		if (effect.options?.scheduler) {
			effect.options.scheduler(forceFlush.bind(null, effect));
		} else {
			forceFlush(effect);
		}
	}
	notifyIndex = 0;
	queuedEffectsLength = 0;
}

function forceFlush(effect: Effect): void {
	run(effect, (effect.flags &= ~EffectFlags.Queued));
}

function computedOper<T>(this: Computed<T>): T {
	const flags = this.flags;
	if (
		flags & (16 satisfies ReactiveFlags.Dirty) ||
		(flags & (32 satisfies ReactiveFlags.Pending) &&
			checkDirty(this.deps!, this))
	) {
		if (updateComputed(this)) {
			const subs = this.subs;
			if (subs !== undefined) {
				shallowPropagate(subs);
			}
		}
	} else if (flags & (32 satisfies ReactiveFlags.Pending)) {
		this.flags = flags & ~(32 satisfies ReactiveFlags.Pending);
	}
	if (activeSub !== undefined) {
		const lastLink = this.subsTail;
		link(this, activeSub);
		const newLink = this.subsTail!;
		if (newLink !== lastLink) {
			const newSub = newLink.sub;
			if (isEffect(newSub) || (isComputed(newSub) && newSub.watchCount)) {
				onWatched(this);
			}
		}
	}
	return this.value!;
}

export function reportChanged(this: Atom) {
	this.flags = 17 as ReactiveFlags.Mutable | ReactiveFlags.Dirty;
	const subs = this.subs;
	if (subs !== undefined) {
		propagate(subs);
		if (!batchDepth) {
			flush();
		}
	}
}

export function reportObserved(this: Atom) {
	if (this.flags & (16 satisfies ReactiveFlags.Dirty)) {
		updateAtom(this);
		const subs = this.subs;
		if (subs !== undefined) {
			shallowPropagate(subs);
		}
	}
	if (activeSub !== undefined) {
		const lastLink = this.subsTail;
		link(this, activeSub);
		const newLink = this.subsTail!;
		if (newLink !== lastLink) {
			const newSub = newLink.sub;
			if (isEffect(newSub) || (isComputed(newSub) && newSub.watchCount)) {
				onWatched(this);
			}
		}
	}
	return this.watchCount > 0;
}

function onWatched(atom: ReactiveNode) {
	if (isAtom(atom)) {
		if (atom.watchCount++ === 0) {
			atom.options?.onBecomeObservedListener?.();
		}
	} else if (isComputed(atom)) {
		if (atom.watchCount++ === 0) {
			atom.options?.onBecomeObservedListener?.();
			for (let link = atom.deps; link !== undefined; link = link.nextDep) {
				const dep = link.dep;
				onWatched(dep);
			}
		}
	}
}

function onUnwatched(atom: ReactiveNode) {
	if (isAtom(atom)) {
		if (--atom.watchCount === 0) {
			atom.options?.onBecomeUnobservedListener?.();
		}
	} else if (isComputed(atom)) {
		if (--atom.watchCount === 0) {
			atom.options?.onBecomeUnobservedListener?.();
			for (let link = atom.deps; link !== undefined; link = link.nextDep) {
				const dep = link.dep;
				onUnwatched(dep);
			}
		}
	}
}

function effectOper(this: Effect): void {
	if (!this.isInitialized) {
		this.isStopped = true;
	}
	onUnwatched(this.computed);
	let dep = this.deps;
	while (dep !== undefined) {
		dep = unlink(dep, this);
	}
	const sub = this.subs;
	if (sub !== undefined) {
		unlink(sub);
	}
	this.flags = 0 satisfies ReactiveFlags.None;
}
